<template>
  <div class="flexContainer">
    <b-row style="width: 100%;">
      <b-col>
        <b-form-group
          label="Status*"
          :state="formErrors.status.length == 0 ? null : false"
          :invalid-feedback="formErrors.status.join(', ')"
        >
          <b-form-select
            v-model="form.status"
            :style="{
              color: statusLabelColor,
              backgroundColor: statusBackgroundColor,
              fontWeight: 'bold'
            }"
            :disabled="disableControls"
          >
            <b-form-select-option
              v-for="status in statuses"
              :key="status.value"
              :value="status.value"
              :style="{
                color: status.data.color,
                backgroundColor: 'white',
                fontWeight: 'bold'
              }"
            >
              {{ status.text }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        <b-form-group
          label="Start date & time"
          :state="formErrors.startDateTime.length == 0 ? null : false"
          :invalid-feedback="formErrors.startDateTime.join(', ')"
        >
          <date-time-picker
            v-model="form.startDateTime"
            :errors="formErrors.startDateTime"
            :max="form.endDateTime ? form.endDateTime : null"
            :disabled="disableControls"
          />
        </b-form-group>
        <b-form-group
          label="End date & time"
          :state="formErrors.endDateTime.length == 0 ? null : false"
          :invalid-feedback="formErrors.endDateTime.join(', ')"
        >
          <date-time-picker
            v-model="form.endDateTime"
            :errors="formErrors.endDateTime"
            :min="form.startDateTime ? form.startDateTime : null"
            :disabled="disableControls"
          />
        </b-form-group>
        <b-form-group
          label="Place of service*"
          :state="formErrors.placeOfService.length == 0 ? null : false"
          :invalid-feedback="formErrors.placeOfService.join(', ')"
        >
          <div class="select-places-of-service">
            <div>
              <v-select
                :clearable="false"
                class=""
                :options="placesOfService"
                v-model="form.placeOfService"
                :disabled="disableControls"
              />
            </div>
          </div>
        </b-form-group>
        <b-form-group label="Room">
          <div class="select_rooms">
            <div>
              <v-select
                :options="rooms"
                v-model="form.room"
                :disabled="disableControls"
              />
            </div>
          </div>
        </b-form-group>
      </b-col>
    </b-row>
    <div v-show="errorMessage" class="text-left text-danger mt-1 mr-auto">
      {{ errorMessage }}
    </div>
    <div
      class="d-flex justify-content-center align-items-center"
      style="height: 50px"
    >
      <b-button
        variant="primary"
        @click="submitScheduleEvent"
        :disabled="disableControls"
      >
        Save
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import scheduleEventServer from "../../../server/scheduleEvent";
import roomServer from "../../../server/scheduleEventRoom";
import userServer from "../../../server/user";
import {
  validateRequired,
  validateDateTime
} from "../../../helpers/validation";
import { lightenDarkenColor } from "../../../helpers/general";
import DateTimePicker from "../../controls/DateTimePicker.vue";
import { formatDatePretty } from "../../../../../Breeze.Frontend/src/helpers/date";
import moment from "moment";

export default {
  name: "DocumentAppointmentTabContent",
  components: {
    DateTimePicker
  },
  props: ["scheduleEventId", "scheduleEventDefaultData"],
  data() {
    return {
      form: {
        status: null,
        startDateTime: null,
        endDateTime: null,
        placeOfService: null,
        room: null
      },
      formErrors: {
        status: [],
        startDateTime: [],
        endDateTime: [],
        placeOfService: []
      },
      rooms: [],
      users: [],
      user: null,
      errorMessage: null,
      scheduleEvent: null
    };
  },
  async mounted() {
    this.fetchPlacesOfService();
    this.fetchRooms();
    await this.fetchUsers();
    await this.fetchScheduleEventStatuses();

    if (this.scheduleEventId) {
      this.scheduleEvent = await scheduleEventServer.get(this.scheduleEventId);
    }

    let form = { ...this.form };
    if (this.scheduleEventId) {
      form = {
        status: this.scheduleEvent.episodesOfCare[0].scheduleEventStatus.id,
        startDateTime: this.formatDateTimeMysql(
          this.scheduleEvent.startDateTime
        ),
        endDateTime: this.formatDateTimeMysql(this.scheduleEvent.endDateTime),
        placeOfService: this.scheduleEvent.placeOfService
          ? {
              value: this.scheduleEvent.placeOfService.id,
              label: `${this.scheduleEvent.placeOfService.code} - ${this.scheduleEvent.placeOfService.name}`,
              data: this.scheduleEvent.placeOfService
            }
          : null,
        room: this.scheduleEvent.room
          ? {
              value: this.scheduleEvent.room.id,
              label: this.scheduleEvent.room.name,
              data: this.scheduleEvent.room
            }
          : null,
        encounterSubmited: this.scheduleEvent.billingInfo?.encounterSubmited,
        teamsMeetingUrl: this.scheduleEvent.teamsMeetingUrl
      };
      this.user =
        this.scheduleEvent.users && this.scheduleEvent.users.length
          ? {
              value: this.scheduleEvent.users[0].id,
              label: this.formatUser(this.scheduleEvent.users[0]),
              data: this.scheduleEvent.users[0]
            }
          : this.users.find(
              user => user.value === this.scheduleEventDefaultData?.providerId
            );
    }
    this.form = { ...form };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchScheduleEventStatuses",
      "fetchPlacesOfService"
    ]),
    validateScheduleEventSubmit() {
      let withoutErrors = true;

      this.formErrors.status = [];
      if (!validateRequired(this.form.status)) {
        this.formErrors.status.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.placeOfService = [];
      if (!validateRequired(this.form.placeOfService)) {
        this.formErrors.placeOfService.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.startDateTime = [];
      if (!validateRequired(this.form.startDateTime)) {
        this.formErrors.startDateTime.push("This field is required");
        withoutErrors = false;
      } else if (!validateDateTime(this.form.startDateTime)) {
        this.formErrors.startDateTime.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(null, new Date(this.form.startDateTime)) ||
        this.checkForOutsideOurs(true)
      ) {
        this.formErrors.startDateTime.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      }

      this.formErrors.endDateTime = [];
      if (!validateRequired(this.form.endDateTime)) {
        this.formErrors.endDateTime.push("This field is required");
        withoutErrors = false;
      } else if (!validateDateTime(this.form.endDateTime)) {
        this.formErrors.endDateTime.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(null, new Date(this.form.endDateTime)) ||
        this.checkForOutsideOurs(false)
      ) {
        this.formErrors.endDateTime.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      } else if (this.form.endDateTime <= this.form.startDateTime) {
        this.formErrors.endDateTime.push("End date must be after start date.");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitScheduleEvent() {
      let checkData = {
        users: this.user ? [this.user.value] : [],
        startDateTime: this.formatDateTimeSql(this.form.startDateTime),
        endDateTime: this.formatDateTimeSql(this.form.endDateTime)
      };
      if (this.scheduleEventId) checkData.id = this.scheduleEventId;

      const noDoubleBooking = await this.checkForDoubleBookedEvent(checkData);
      let doubleBookingAlert = {
        data: {
          value: true
        }
      };
      if (noDoubleBooking.doubleBooked) {
        try {
          doubleBookingAlert = await this.$dialog.confirm(
            {
              title: "This provider already has an appointment at this time."
            },
            {
              view: "multiple-actions",
              cancelText: "Keep editing",
              options: [{ value: true, label: "Save" }]
            }
          );
        } catch (error) {
          return;
        }
      }
      const result = await this.validateScheduleEventSubmit();
      if (result && doubleBookingAlert.data.value === true) {
        var formData = {
          type: "Appointment",
          startDateTime: this.formatDateTimeSql(this.form.startDateTime),
          endDateTime: this.formatDateTimeSql(this.form.endDateTime),
          placeOfServiceId: this.form.placeOfService
            ? this.form.placeOfService.value
            : null,
          roomId: this.form.room ? this.form.room.value : null,
          users: this.user ? [this.user.value] : [],
          scheduleTeamsMeeting: !!this.form.teamsMeetingUrl,
          episodesOfCare: [
            {
              id: this.scheduleEvent.episodesOfCare[0].id,
              episodeOfCareId: this.scheduleEvent.episodesOfCare[0]
                .episodeOfCare.id,
              icdCodeIds: this.scheduleEvent.episodesOfCare[0].icdCodes.map(
                c => c.id
              ),
              statusId: this.form.status,
              primaryPatientInsuranceId: this.scheduleEvent.episodesOfCare[0]
                .primaryPatientInsuranceId,
              secondaryPatientInsuranceId: this.scheduleEvent.episodesOfCare[0]
                .secondaryPatientInsuranceId,
              tertiaryPatientInsuranceId: this.scheduleEvent.episodesOfCare[0]
                .tertiaryPatientInsuranceId
            }
          ]
        };

        if (this.scheduleEventId) {
          try {
            const dialog = await this.$dialog.confirm(
              {
                title: "Do you want to notify participants?"
              },
              {
                view: "multiple-actions",
                options: [
                  { value: true, label: "Yes" },
                  { value: false, label: "No" }
                ]
              }
            );
            formData.resend = dialog.data.value;
            await scheduleEventServer.update(this.scheduleEventId, formData);
          } catch (error) {
            if (error.response.data?.date) {
              this.errorMessage = `${
                error.response.data.message
              } ${formatDatePretty(error.response.data.date)}`;
            } else {
              this.errorMessage =
                error.response.data.errorCode === "tooMuchOcurrences"
                  ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                  : error.response.data.message;
            }
            return;
          }
        }

        this.showAlertSuccess();
        this.$emit("onModify");
      } else {
        //this.$dialog.alert("You have some errors in the form");
      }
    },
    async fetchUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users
        .filter(user => user.providerInfo)
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }));
    },
    async fetchRooms() {
      const rooms = await roomServer.query();
      this.rooms = rooms.map(room => ({
        value: room.id,
        label: room.name,
        data: room
      }));
    },
    async checkForDoubleBookedEvent(data) {
      return await scheduleEventServer
        .checkForDoubleBookedEvent(data)
        .then(result => {
          return result;
        });
    },
    checkForOutsideOurs(start) {
      if (start) {
        const startDateTimeHours = moment(this.form.startDateTime).hour();
        const startDateTimeMinutes = moment(this.form.startDateTime).minute();
        const opsStart = this.selectedLocation.operationStarts.split(":");
        return startDateTimeHours < parseInt(opsStart[0])
          ? true
          : startDateTimeHours == parseInt(opsStart[0]) &&
            startDateTimeMinutes < parseInt(opsStart[1])
          ? true
          : false;
      } else {
        const endDateTimeHours = moment(this.form.endDateTime).hour();
        const endDateTimeMinutes = moment(this.form.endDateTime).minute();
        const opsEnds = this.selectedLocation.operationEnds.split(":");
        return endDateTimeHours > parseInt(opsEnds[0])
          ? true
          : endDateTimeHours == parseInt(opsEnds[0]) &&
            endDateTimeMinutes > parseInt(opsEnds[1])
          ? true
          : false;
      }
    }
  },
  computed: {
    ...mapGetters("layout", [
      "datesNonWorking",
      "getCurrentAdjustedDateTime",
      "canEditChargedEvents"
    ]),
    ...mapState("client", {
      statuses: state =>
        state.scheduleEventStatuses.map(status => ({
          value: status.id,
          text: status.name,
          data: status
        })),
      placesOfService: state =>
        state.placesOfService.map(placeOfService => ({
          value: placeOfService.id,
          label: `${placeOfService.code}-${placeOfService.name}`,
          data: placeOfService
        }))
    }),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      isPracticeSuiteActive: state => state.isPracticeSuiteActive,
      isZoomEnabled: state => state.systemSetting.isZoomEnabled,
      isTeamsEnabled: state => state.systemSetting.isTeamsEnabled,
      teleHealtAutomaticModifier: state =>
        state.systemSetting.enableTelehealthSetMod
    }),
    ...mapGetters("client", ["selectedLocation"]),
    providerUsers() {
      return this.isPracticeSuiteActive
        ? this.users.filter(u => u.data.providerInfo)
        : this.users;
    },
    statusLabelColor() {
      if (this.statuses.length == 0 || !this.form.status) {
        return "";
      }
      return this.statuses.find(u => u.value == this.form.status).data.color;
    },
    statusBackgroundColor() {
      if (this.statuses.length == 0 || !this.form.status) {
        return "";
      }
      return lightenDarkenColor(
        this.statuses.find(u => u.value == this.form.status).data.color,
        85
      );
    },
    disableControls() {
      return this.form.encounterSubmited && !this.canEditChargedEvents;
    }
  }
};
</script>

<style>
.flexContainer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: space-between;
  justify-content: center;
  height: 100%;
}
</style>
